/**
 * AdminAiMySubmissionsSubtab — an author's own Ion knowledge submissions.
 *
 * The author's status board: every proposal they submitted with its review
 * status and, on a rejection, the reviewer's note. A rejected ("Needs changes")
 * proposal can be reopened right here via "Edit & resubmit" — the editor opens
 * prefilled with the proposed content so the author revises and resubmits for
 * another review (author-driven resubmit model). On resubmit the backend
 * supersedes the old rejected row, so each topic shows one current item.
 *
 * Data: GET /admin/ion-knowledge/my-proposals (any knowledge author —
 * admin or knowledge_expert; no approver capability needed).
 */
import { useEffect, useState } from 'preact/hooks'
import {
  listMyKnowledgeProposals,
  getKnowledgeCapabilities,
} from '../../services/ionKnowledgeService.js'
import { KnowledgeEntryEditor } from './AdminAiKnowledgeSubtab.jsx'

function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
}

const ACTION_LABELS = { create: 'New entry', update: 'Edit', deactivate: 'Removal' }
const STATUS_LABELS = {
  pending: 'Awaiting review',
  approved: 'Published',
  rejected: 'Needs changes',
  superseded: 'Superseded',
}

function statusPill(status) {
  const cls = `admin-aim-sub-pill is-${status || 'pending'}`
  return <span className={cls}>{STATUS_LABELS[status] || status}</span>
}

// Flow proposals are `<slug>_selection` knowledge entries — they carry a YAML
// spec, so they must be revised in the structured AI Flows editor, not the prose
// KnowledgeEntryEditor.
function isFlowProposal(p) {
  return (p.topic_id || p.proposed_topic_id || '').endsWith('_selection')
}

// A rejected create/update proposal can be edited + resubmitted inline. Flow
// proposals are excluded (revise them in the AI Flows tab). Deactivate
// rejections aren't resubmittable here (re-proposing a deletion is unusual;
// the author can do it from the Knowledge tab if they really want to).
function canResubmit(p) {
  return (
    p.status === 'rejected' &&
    (p.action === 'create' || p.action === 'update') &&
    !isFlowProposal(p)
  )
}

// Build the KnowledgeEntryEditor mode + initial from a rejected proposal so the
// author picks up exactly what they proposed (not the live entry).
function editorSeed(p) {
  const proposed = p.proposed || {}
  if (p.action === 'create') {
    return {
      mode: 'create',
      initial: {
        topic_id: p.proposed_topic_id || '',
        title: proposed.title || '',
        summary: proposed.summary || '',
        body_md: proposed.body_md || '',
        keywords: proposed.keywords || '',
        applies_to: proposed.applies_to || '',
      },
    }
  }
  return {
    mode: 'edit',
    initial: {
      topic_id: p.topic_id,
      title: proposed.title || '',
      summary: proposed.summary || '',
      body_md: proposed.body_md || '',
      keywords: proposed.keywords || '',
      applies_to: proposed.applies_to || '',
      is_active: proposed.is_active ?? true,
    },
  }
}

export function AdminAiMySubmissionsSubtab() {
  const [proposals, setProposals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [canPublish, setCanPublish] = useState(false)
  const [resubmitting, setResubmitting] = useState(null) // proposal being edited

  const refresh = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listMyKnowledgeProposals()
      setProposals(data.proposals || [])
    } catch (e) {
      setError(e?.message || 'network')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    getKnowledgeCapabilities()
      .then((c) => setCanPublish(c.can_publish_directly !== false))
      .catch(() => {})
  }, [])

  const seed = resubmitting ? editorSeed(resubmitting) : null

  return (
    <div className="admin-aim-subtab-body">
      <header className="admin-aim-header">
        <div>
          <h2 className="admin-aim-title">My Submissions</h2>
          <p className="admin-aim-sub">
            Knowledge changes you submitted for review. An approver publishes
            them to Ion or sends them back with a note. When something needs
            changes, use <strong>Edit &amp; resubmit</strong> to revise it right
            here.
          </p>
        </div>
        <button type="button" className="admin-ai-btn admin-ai-btn--ghost" onClick={refresh}>↻</button>
      </header>

      {error && <div className="admin-aim-error-banner">{error}</div>}

      <div className="admin-aim-table-wrap">
        <table className="admin-aim-table">
          <thead>
            <tr>
              <th>Change</th>
              <th>Topic</th>
              <th>Status</th>
              <th>Submitted</th>
              <th>Reviewed</th>
              <th>Reviewer note</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && proposals.length === 0 ? (
              <tr><td colSpan={7} className="admin-aim-empty">Loading…</td></tr>
            ) : proposals.length === 0 ? (
              <tr><td colSpan={7} className="admin-aim-empty">
                You haven't submitted any knowledge changes yet. Create or edit an
                entry in the Knowledge tab — it shows up here for review.
              </td></tr>
            ) : proposals.map((p) => {
              const topic = p.topic_id || p.proposed_topic_id || '—'
              return (
                <tr key={p.id} className={p.status === 'superseded' ? 'admin-aim-row--muted' : ''}>
                  <td>{ACTION_LABELS[p.action] || p.action}</td>
                  <td>
                    <div className="admin-aim-user-cell">
                      <span className="admin-aim-user-email">
                        {p.proposed?.title || topic}
                        {isFlowProposal(p) && <span className="admin-aim-flow-tag">AI Flow</span>}
                      </span>
                      <span className="admin-aim-user-name"><code>{topic}</code></span>
                    </div>
                  </td>
                  <td>{statusPill(p.status)}</td>
                  <td className="admin-aim-rel">{formatDate(p.created_at)}</td>
                  <td className="admin-aim-rel">{formatDate(p.reviewed_at)}</td>
                  <td>{p.status === 'rejected' ? (p.review_note || '—') : '—'}</td>
                  <td>
                    {canResubmit(p) && (
                      <button
                        type="button"
                        className="admin-ai-btn admin-ai-btn--ghost"
                        onClick={() => setResubmitting(p)}
                      >
                        Edit &amp; resubmit
                      </button>
                    )}
                    {p.status === 'rejected' && isFlowProposal(p) && (
                      <span className="admin-aim-field-hint">Revise in the AI Flows tab</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {resubmitting && (
        <KnowledgeEntryEditor
          mode={seed.mode}
          initial={seed.initial}
          canPublish={canPublish}
          onClose={() => setResubmitting(null)}
          onSaved={() => {
            setResubmitting(null)
            refresh()
          }}
        />
      )}
    </div>
  )
}
